import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Popover,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudDone as CloudDoneIcon,
  Error as ErrorIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { getInitials } from "@/pages/Board/utils";

interface RetrospectiveHeaderProps {
  sprintName: string;
  sprintId: string;
  templateName: string;
  connected?: boolean;
  users: any;
  saveStatus?: "idle" | "saving" | "saved" | "error";
  isLoading?: boolean;
  lastSaveTime?: Date | null;
  onBack: () => void;
  onChangeTemplate?: () => void;
  onReset?: () => void;
}

const RetrospectiveHeader: React.FC<RetrospectiveHeaderProps> = ({
  sprintName,
  sprintId,
  templateName,
  connected,
  users,
  saveStatus,
  isLoading = false,
  lastSaveTime,
  onBack,
  onChangeTemplate,
  onReset,
}) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // New state for confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleUserCountClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Open confirmation dialog instead of window.confirm
  const handleResetClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };

  const handleConfirmReset = () => {
    setConfirmOpen(false);
    onReset?.();
  };

  // Convert users Map to array for rendering
  const usersArray = users ? Array.from(users.entries()) : [];

  return (
    <>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left Section - Back button and Sprint Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={onBack}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 500, color: "#333", mt: 0.5 }}
            >
              Sprint Retrospective Session
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: connected ? "#4caf50" : "#f44336",
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {connected ? "Connected" : "Disconnected"}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                {sprintName}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {new Date().toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  background: theme.palette.primary.main,
                  color: "white",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                }}
              >
                {templateName}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Section - Status, Reset Button, and Change Template */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "space-between",
            mr: 1.5,
          }}
        >
          <Typography
            variant="caption"
            display="block"
            onClick={handleUserCountClick}
            sx={{
              color: "green",
              fontWeight: "500",
              fontSize: "0.75rem",
              marginBottom: 0.5,
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {users?.size || 0} user{users?.size !== 1 ? "s" : ""} online
          </Typography>

          {/* Users Popover */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            sx={{
              mt: 1,
            }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 600 }}
              >
                Online Users ({users?.size || 0})
              </Typography>
              {usersArray.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {usersArray.map(([userId, userData], index) => (
                    <ListItem key={userId} sx={{ px: 0, py: 0.5 }}>
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar
                          sx={{
                            backgroundColor: userData.color || "#1976d2",
                            width: "24px",
                            height: "24px",
                            fontSize: "10px",
                            fontWeight: 500,
                          }}
                        >
                          {getInitials(userData.name)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={userData.name}
                        primaryTypographyProps={{
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No users online
                </Typography>
              )}
            </Box>
          </Popover>

          {/* Last Save Time */}
          {lastSaveTime && saveStatus === "idle" && (
            <Typography
              variant="caption"
              sx={{ color: "#666", fontSize: "0.7rem" }}
            >
              Last saved: {lastSaveTime.toLocaleTimeString()}
            </Typography>
          )}

          {/* Connection and User Status */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            {/* Save Status */}
            {(isLoading || saveStatus !== "idle") && (
              <Chip
                icon={
                  isLoading ? (
                    <span>🔄</span>
                  ) : saveStatus === "saving" ? (
                    <SaveIcon fontSize="small" />
                  ) : saveStatus === "saved" ? (
                    <CloudDoneIcon fontSize="small" />
                  ) : saveStatus === "error" ? (
                    <ErrorIcon fontSize="small" />
                  ) : null
                }
                label={
                  isLoading
                    ? "Loading..."
                    : saveStatus === "saving"
                    ? "Saving..."
                    : saveStatus === "saved"
                    ? "Saved"
                    : saveStatus === "error"
                    ? "Save Error"
                    : ""
                }
                color={
                  saveStatus === "saved"
                    ? "success"
                    : saveStatus === "error"
                    ? "error"
                    : "primary"
                }
                size="small"
                sx={{ height: 20, fontSize: "0.75rem" }}
              />
            )}
          </Box>

          {/* Reset Button */}
          {onReset && (
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleResetClick}
              sx={{
                textTransform: "none",
              }}
            >
              Reset
            </Button>
          )}
        </Box>
      </Box>

      {/* Confirmation Dialog for reset */}
      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        aria-labelledby="confirm-reset-dialog-title"
        aria-describedby="confirm-reset-dialog-description"
      >
        <DialogTitle id="confirm-reset-dialog-title">
          Reset Retrospective
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-reset-dialog-description">
            Are you sure you want to reset the retrospective to the default
            state? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmReset} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RetrospectiveHeader;
